import { IconIntroduction } from '@consta/icons/IconIntroduction'
import type { Props } from '@consta/uikit/Button'
import { Button } from '@consta/uikit/Button'
import { FieldGroup } from '@consta/uikit/FieldGroup'
import { TextField } from '@consta/uikit/TextField'
import { withTooltip } from '@consta/uikit/withTooltip'
import { selectActiveEntity, useAppSelector } from '@core/core/store'
import { getSimpleTooltipContent } from '@library/utils'
import classNames from 'classnames'
import type { BaseObject, Nullable } from 'common/types'
import type { FC } from 'react'

import {
  IconGeoObjectMarker,
  IconGeoObjFault,
  IconGeoObjHorizon,
  IconGeoObjSeismic2D,
  IconGeoObjSeismic3D,
  IconIronMap,
  IconPolygon,
} from '../../icons'
import css from './TreeInput.css'
import type { TreeInputProps } from './types'

const ButtonWithTooltip = withTooltip({ direction: 'downRight', appearTimeout: 300 })(Button)

const buttonProps: Partial<Props & { className: string }> = {
  className: css.button,
  view: 'secondary',
  onlyIcon: true,
  iconSize: 'xs',
  iconLeft: IconIntroduction,
}

function getIcon(type?: BaseObject, color?: string) {
  const iconColor = { color }

  switch (type) {
    case 'cube':
      return <IconGeoObjSeismic3D size="s" />
    case 'interpretation3D':
    case 'horizon':
      return <IconGeoObjHorizon size="s" style={iconColor} />
    case 'survey2d':
      return <IconGeoObjSeismic2D size="s" />
    case 'polygon':
      return <IconPolygon style={iconColor} size="s" />
    case 'map':
      return <IconIronMap style={iconColor} />
    case 'marker':
      return <IconGeoObjectMarker style={iconColor} />
    case 'faultsFolder':
      return <IconGeoObjFault style={iconColor} />
    default:
      return null
  }
}

export const TreeInput: FC<TreeInputProps> = ({
  value,
  allowedTypes,
  onChange,
  tooltip,
  color,
  treeInputProps,
  chooseEntityNameInsteadOfId = false,
  status,
  caption,
}) => {
  const baseEntity = useAppSelector(selectActiveEntity)

  const onClick = () => {
    onChange(baseEntity)
  }
  const clearValue = (value: Nullable<string>) => {
    if (value == null) {
      onChange(null)
    }
  }
  const isEntityExist = chooseEntityNameInsteadOfId ? baseEntity?.entityName : baseEntity?.entityId
  const isEntityTypeAllowed = baseEntity ? allowedTypes.includes(baseEntity.entityType) : false
  const disabled = !isEntityExist || !isEntityTypeAllowed
  const actionProps = { onClick, disabled }

  const ButtonToShow = tooltip ? (
    <ButtonWithTooltip
      {...buttonProps}
      {...actionProps}
      tooltipProps={{
        tooltipContent: getSimpleTooltipContent(tooltip),
      }}
    />
  ) : (
    <Button {...buttonProps} {...actionProps} />
  )
  const icon = getIcon(value?.entityType, color ?? value?.customProperties?.fillColor)

  return (
    <FieldGroup size="xs" className={css.container}>
      {ButtonToShow}
      <TextField
        status={status}
        caption={caption}
        placeholder="Выбрать"
        leftSide={() => icon}
        value={value?.entityName ?? ''}
        withClearButton={true}
        onChange={clearValue}
        {...treeInputProps}
        className={classNames(css.textField, treeInputProps?.className)}
      />
    </FieldGroup>
  )
}
